/* Behaviour for the shop / collection sections.
   No dependencies. Safe to load twice and safe to re-run after the theme
   editor swaps a section in.

   The quick view keeps its own variant/gallery/quantity code rather than
   reusing minery-product.js: that file rewrites the address bar with the
   selected ?variant=, which is right on a product page and wrong on a
   collection page. Its data attributes are namespaced data-qv-* so the two
   never bind to the same element. */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Circular collection carousel.
     Arrows appear only when the row actually overflows.
     ---------------------------------------------------------------------- */

  function initCircles(root) {
    if (root.dataset.mReady === '1') return;
    root.dataset.mReady = '1';

    var track = root.querySelector('[data-m-circtrack]');
    if (!track) return;

    function measure() {
      root.classList.toggle(
        'is-scrollable',
        track.scrollWidth > track.clientWidth + 2
      );
    }

    root.querySelectorAll('[data-m-circnav]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.dataset.mCircnav === 'next' ? 1 : -1;
        track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
      });
    });

    /* The active circle carries aria-current, but nothing ever brought it into
       view. On a phone the arrows are hidden, so opening a collection late in
       the row showed the row's first circles, no arrows, and no sign of where
       you were — measured on /collections/gifts-under-999 at 375px, the active
       circle sat 205px past the right edge with scrollLeft still 0. */
    var userTookOver = false;

    /* Only real input counts. Listening for 'scroll' would also catch the
       scroll this code performs itself. */
    ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      track.addEventListener(ev, function () { userTookOver = true; }, { passive: true });
    });

    function centreActive() {
      if (userTookOver) return;
      if (!root.classList.contains('is-scrollable')) return;

      var active = track.querySelector('[aria-current]');
      if (!active) return;

      var trackBox = track.getBoundingClientRect();
      var itemBox = active.getBoundingClientRect();
      var target = track.scrollLeft +
        (itemBox.left - trackBox.left) -
        (track.clientWidth - itemBox.width) / 2;

      // Clamped, so the first and last circles stay flush with their end
      // rather than being pulled to the middle against empty space.
      target = Math.max(0, Math.min(target, track.scrollWidth - track.clientWidth));
      if (Math.abs(target - track.scrollLeft) < 1) return;

      /* The track sets scroll-behavior: smooth for the arrow buttons, which
         would animate this on first paint. A starting position should just be
         there, so the behaviour is suspended for the jump and put back. */
      var previous = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = target;
      track.style.scrollBehavior = previous;
    }

    function update() {
      measure();
      centreActive();
    }

    update();
    window.addEventListener('resize', update);

    // Images load after first paint and change the row's width.
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });
  }

  /* ----------------------------------------------------------------------
     Sidebar toggle.

     On a phone the sidebar is a panel this button opens; on a desktop it is
     a column this button hides. Same control, two jobs, so the class it
     writes depends on the viewport.
     ---------------------------------------------------------------------- */

  function initFilterToggle(btn) {
    if (btn.dataset.mReady === '1') return;
    btn.dataset.mReady = '1';

    var shop = btn.closest('[data-m-shop]') || document.querySelector('[data-m-shop]');
    if (!shop) return;

    var small = window.matchMedia('(max-width: 900px)');
    var sheet = shop.querySelector('[data-m-filtersheet]');
    var handle = shop.querySelector('[data-m-filterdrag]');

    function isOpen() {
      return small.matches
        ? shop.classList.contains('is-side-open')
        : !shop.classList.contains('is-side-hidden');
    }

    function sync() {
      btn.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');
      if (!sheet) return;
      // A dialog only while it is a sheet; on a desktop it is a sidebar.
      if (small.matches) {
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
      } else {
        sheet.removeAttribute('role');
        sheet.removeAttribute('aria-modal');
        shop.classList.remove('is-side-open');
        document.documentElement.classList.remove('m-fs-lock');
      }
    }

    function open() {
      shop.classList.add('is-side-open');
      document.documentElement.classList.add('m-fs-lock');
      sync();
      var close = sheet && sheet.querySelector('.m-fs__close');
      // After the frame that starts the transition, or focus scrolls the
      // still-offscreen sheet into view and the rise is lost.
      if (close) requestAnimationFrame(function () { close.focus({ preventScroll: true }); });
    }

    function close() {
      shop.classList.remove('is-side-open');
      document.documentElement.classList.remove('m-fs-lock');
      if (sheet) sheet.style.transform = '';
      sync();
      btn.focus({ preventScroll: true });
    }

    btn.addEventListener('click', function () {
      if (small.matches) {
        if (shop.classList.contains('is-side-open')) close(); else open();
      } else {
        shop.classList.toggle('is-side-hidden');
        sync();
      }
    });

    shop.querySelectorAll('[data-m-filterclose]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (shop.classList.contains('is-side-open')) close();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && small.matches && shop.classList.contains('is-side-open')) close();
    });

    /* Drag the head down to dismiss. The sheet follows the finger 1:1 while
       it moves; dragging up past the resting point meets resistance instead
       of a wall. On release it closes if it travelled past a third of its
       height OR was flicked downward faster than 0.11px/ms — a quick flick is
       enough, the finger does not have to go all the way. Otherwise it
       springs back on the normal open curve. */
    if (sheet && handle && window.PointerEvent) {
      var startY = 0;
      var lastY = 0;
      var lastT = 0;
      var velocity = 0;
      var dragging = false;

      handle.addEventListener('pointerdown', function (e) {
        if (!shop.classList.contains('is-side-open')) return;
        if (e.target.closest('button')) return;
        dragging = true;
        startY = lastY = e.clientY;
        lastT = e.timeStamp;
        velocity = 0;
        try { handle.setPointerCapture(e.pointerId); } catch (err) { /* keep dragging without capture */ }
        sheet.classList.add('is-dragging');
      });

      handle.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dy = e.clientY - startY;
        if (dy < 0) dy = dy * 0.2;
        var dt = e.timeStamp - lastT;
        if (dt > 0) velocity = (e.clientY - lastY) / dt;
        lastY = e.clientY;
        lastT = e.timeStamp;
        sheet.style.transform = 'translateY(' + dy + 'px)';
      });

      function release(e) {
        if (!dragging) return;
        dragging = false;
        sheet.classList.remove('is-dragging');
        var dy = e.clientY - startY;
        if (dy > sheet.offsetHeight / 3 || velocity > 0.11) {
          close();
        } else {
          sheet.style.transform = '';
        }
      }

      handle.addEventListener('pointerup', release);
      handle.addEventListener('pointercancel', release);
    }

    if (small.addEventListener) small.addEventListener('change', sync);
    sync();
  }

  /* ----------------------------------------------------------------------
     Grid density toggle. Remembered per browser.
     ---------------------------------------------------------------------- */

  function initViewToggle(btn) {
    if (btn.dataset.mReady === '1') return;
    btn.dataset.mReady = '1';

    var grid = document.querySelector('[data-m-productgrid]');
    if (!grid) return;

    var wide = parseInt(btn.dataset.mViewWide || '4', 10);
    var narrow = parseInt(btn.dataset.mViewNarrow || '2', 10);
    var stored = null;

    try {
      stored = window.localStorage.getItem('m-shop-cols');
    } catch (err) {
      stored = null;
    }

    function apply(cols) {
      grid.style.setProperty('--m-cols', String(cols));
      btn.setAttribute('aria-pressed', cols === narrow ? 'true' : 'false');
      try {
        window.localStorage.setItem('m-shop-cols', String(cols));
      } catch (err) {
        /* storage disabled — the toggle still works for this page */
      }
    }

    if (stored === String(narrow)) apply(narrow);

    btn.addEventListener('click', function () {
      var current = parseInt(grid.style.getPropertyValue('--m-cols'), 10) || wide;
      apply(current === narrow ? wide : narrow);
    });
  }

  /* ----------------------------------------------------------------------
     Sort: submit on change. The <noscript> button covers the other case.
     ---------------------------------------------------------------------- */

  function initSort(form) {
    if (form.dataset.mReady === '1') return;
    form.dataset.mReady = '1';

    var select = form.querySelector('select');
    if (!select) return;

    select.addEventListener('change', function () {
      form.submit();
    });
  }

  /* ----------------------------------------------------------------------
     Quick view
     ---------------------------------------------------------------------- */

  var dialog = null;
  var lastFocus = null;
  var qvToken = null;
  var qvCache = {};

  // The card photo the shopper tapped, already in the browser's cache. It
  // stands in for the gallery while the quick view loads, so the sheet opens
  // on the picture instead of an empty square.
  var qvPhoto = '';

  // Opened by a tap or a click rather than the keyboard. Focus still moves
  // into the dialog and back to the trigger, for screen readers, but without
  // a ring: showModal() put one round the close button on every open, and
  // Safari on iPhone drew one round the square quick view button.
  var qvByPointer = false;

  // Same breakpoint as the filter sheet: at 900px and below the dialog is a
  // bottom sheet, and dragging its grabber down dismisses it.
  var qvSheet = window.matchMedia('(max-width: 900px)');

  function qvReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function finishClose() {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  // Leaving is quicker than arriving (200ms, 260ms as a sheet), so the page
  // answers at once. The out animations only name their end state, which
  // lets a sheet dragged halfway down carry on from where the finger left it.
  function closeDialog() {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    if (qvReducedMotion()) {
      finishClose();
      return;
    }

    var done = false;
    function end() {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', onEnd);
      finishClose();
    }
    function onEnd(event) {
      if (event.target === dialog) end();
    }

    dialog.addEventListener('animationend', onEnd);
    dialog.classList.add('is-closing');
    window.setTimeout(end, 400);
  }

  function openDialog() {
    if (!dialog) return;
    dialog.classList.remove('is-closing');
    dialog.style.transform = '';
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    // showModal() focuses the close button, which then opened ringed as if
    // already chosen. Opened by a click or a tap, the dialog itself takes
    // focus instead; opened from the keyboard, the close button keeps it.
    if (qvByPointer) {
      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
  }

  /* Product videos in the quick view (snippets/minery-video-media): only the
     slide on screen plays, muted and looping, and everything pauses when the
     dialog closes. Mirrors the helpers in minery-product.js. */
  function qvSyncVideos(scope, activeSlide) {
    scope.querySelectorAll('[data-m-video]').forEach(function (box) {
      var video = box.querySelector('video');
      if (!video) return;
      if (activeSlide && activeSlide.contains(box)) {
        if (qvReducedMotion()) {
          video.controls = true;
          return;
        }
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      } else {
        video.pause();
      }
    });
  }

  function qvInitVideoSound(scope) {
    scope.querySelectorAll('[data-m-video-sound]').forEach(function (btn) {
      if (btn.dataset.mReady === '1') return;
      btn.dataset.mReady = '1';
      btn.addEventListener('click', function () {
        var video = btn.closest('[data-m-video]').querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        if (!video.muted && video.paused) {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        }
        btn.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
        btn.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
      });
    });
  }

  /* gallery inside the dialog: one photograph at a time on a scroll-snap
     track, so a phone swipes it and a trackpad scrolls it sideways; arrows
     and a counter for a mouse. Returns show(mediaId) for the variant code. */
  function initQvGallery(root) {
    var track = root.querySelector('[data-qv-track]');
    if (!track) return null;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-qv-stage]'));
    if (slides.length === 0) return null;

    var prev = root.querySelector('[data-qv-prev]');
    var next = root.querySelector('[data-qv-next]');
    var count = root.querySelector('[data-qv-count]');

    var colorSlides = [];
    if (track.hasAttribute('data-qv-color-track')) {
      var variantData = root.querySelector('[data-qv-variants]');
      var variants = variantData ? JSON.parse(variantData.textContent) : [];
      colorSlides = slides.filter(function (slide) {
        return variants.some(function (variant) {
          return String(variant.media_id) === slide.dataset.qvStage;
        });
      });
    }

    function show(mediaId) {
      var colorSlide = colorSlides.find(function (slide) {
        return slide.dataset.qvStage === mediaId;
      });
      if (colorSlide) {
        colorSlides.forEach(function (slide) { slide.remove(); });
        track.insertBefore(colorSlide, track.firstChild);
        slides = Array.prototype.slice.call(track.querySelectorAll('[data-qv-stage]'));
      }
      for (var n = 0; n < slides.length; n++) {
        if (slides[n].dataset.qvStage === mediaId) {
          go(n, true);
          sync();
          return;
        }
      }
    }

    function current() {
      var width = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
    }

    function sync() {
      var index = current();
      if (count) count.textContent = index + 1 + ' / ' + slides.length;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      qvSyncVideos(track, slides[index]);
    }

    qvInitVideoSound(track);
    var qvDialog = root.closest('dialog');
    if (qvDialog) {
      qvDialog.addEventListener('close', function () { qvSyncVideos(track, null); }, { once: true });
    }

    function go(index, instant) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: instant || qvReducedMotion() ? 'auto' : 'smooth'
      });
    }

    var frame = 0;
    track.addEventListener(
      'scroll',
      function () {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(sync);
      },
      { passive: true }
    );

    if (prev) prev.addEventListener('click', function () { go(current() - 1); });
    if (next) next.addEventListener('click', function () { go(current() + 1); });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(current() - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(current() + 1);
      }
    });

    // A photograph fades in when it arrives instead of popping over the tile.
    // The first one fades in over the card photo the skeleton was showing,
    // so the picture never blinks out between the two.
    slides.forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img || img.complete) return;
      if (qvPhoto && slide.hasAttribute('data-qv-first')) {
        slide.style.backgroundImage = 'url("' + qvPhoto.replace(/"/g, '%22') + '")';
        slide.classList.add('has-standin');
      }
      slide.classList.add('is-loading');
      function shown() {
        slide.classList.remove('is-loading');
      }
      img.addEventListener('load', shown, { once: true });
      img.addEventListener('error', shown, { once: true });
    });

    for (var i = 0; i < slides.length; i++) {
      if (slides[i].hasAttribute('data-qv-first')) {
        show(slides[i].dataset.qvStage);
        break;
      }
    }
    sync();

    return show;
  }

  /* Drag the grabber down to dismiss the sheet — the same rules as the
     filter sheet: 1:1 under the finger, resistance above the resting point,
     and it closes past a third of its height or on a downward flick faster
     than 0.11px/ms. Otherwise it springs back on the sheet's own curve. */
  function initQvDrag(root) {
    var grab = root.querySelector('[data-qv-grab]');
    if (!grab || !window.PointerEvent) return;

    var startY = 0;
    var lastY = 0;
    var lastT = 0;
    var velocity = 0;
    var dragging = false;

    grab.addEventListener('pointerdown', function (event) {
      if (!qvSheet.matches || !dialog || dialog.classList.contains('is-closing')) return;
      dragging = true;
      startY = lastY = event.clientY;
      lastT = event.timeStamp;
      velocity = 0;
      try { grab.setPointerCapture(event.pointerId); } catch (err) { /* keep dragging without capture */ }
      dialog.classList.add('is-dragging');
    });

    grab.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var dy = event.clientY - startY;
      if (dy < 0) dy = dy * 0.2;
      var dt = event.timeStamp - lastT;
      if (dt > 0) velocity = (event.clientY - lastY) / dt;
      lastY = event.clientY;
      lastT = event.timeStamp;
      dialog.style.transform = 'translateY(' + dy + 'px)';
    });

    function release(event) {
      if (!dragging) return;
      dragging = false;
      dialog.classList.remove('is-dragging');
      var dy = event.clientY - startY;
      if (dy > dialog.offsetHeight / 3 || velocity > 0.11) {
        closeDialog();
      } else {
        dialog.style.transform = '';
      }
    }

    grab.addEventListener('pointerup', release);
    grab.addEventListener('pointercancel', release);
  }

  /* The heart arrives inside fetched markup, after minery-wishlist.js has
     already marked the page's hearts, so its saved state is read here from
     the same localStorage key that script writes. Clicks need nothing: that
     script listens on the document. */
  function initQvWish(root) {
    var heart = root.querySelector('[data-wishlist-add]');
    if (!heart) return;

    var saved = false;
    try {
      var list = JSON.parse(window.localStorage.getItem('minery:wishlist'));
      saved = Array.isArray(list) && list.some(function (item) {
        return item && item.handle === heart.dataset.productHandle;
      });
    } catch (err) {
      /* storage blocked — the heart starts empty */
    }

    heart.classList.toggle('is-saved', saved);
    heart.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }

  function initQvVariants(root, showMedia) {
    var dataEl = root.querySelector('[data-qv-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    if (!variants || variants.length === 0) return;

    var idInput = root.querySelector('[name="id"]');
    var priceEl = root.querySelector('[data-qv-price]');
    var button = root.querySelector('[data-qv-atc]');
    var buttonText = button ? button.querySelector('span') : null;
    var soldOut = root.dataset.qvSoldOut || 'Sold out';
    var addLabel = root.dataset.qvAddLabel || 'Add to cart';

    function selected() {
      return Array.prototype.slice
        .call(root.querySelectorAll('[data-qv-option]:checked'))
        .map(function (input) {
          return input.value;
        });
    }

    function match(values) {
      return variants.filter(function (v) {
        return (
          v.options.length === values.length &&
          v.options.every(function (option, i) {
            return option === values[i];
          })
        );
      })[0];
    }

    // A row of circles cannot name the colour it is showing, so each swatch
    // row carries the chosen value as text. This runs ahead of the variant
    // lookup so the name still tracks the click on a combination that has
    // no variant behind it.
    function syncPicked() {
      root.querySelectorAll('[data-qv-picked]').forEach(function (label) {
        var group = label.closest('.m-qv__opt');
        if (!group) return;
        var checked = group.querySelector('[data-qv-option]:checked');
        if (checked) label.textContent = checked.value;
      });
    }

    function update() {
      syncPicked();

      var variant = match(selected());

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
        if (buttonText) buttonText.textContent = soldOut;
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.innerHTML = variant.price_html;

      if (button) {
        button.disabled = !variant.available;
        button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        if (buttonText) buttonText.textContent = variant.available ? addLabel : soldOut;
      }

      if (showMedia && variant.media_id) showMedia(String(variant.media_id));
    }

    root.querySelectorAll('[data-qv-option]').forEach(function (input) {
      input.addEventListener('change', update);
    });

    // Same as the product page: hover previews the name where the chosen
    // name lives, in place of the browser's dark title tooltip.
    root.querySelectorAll('[data-qv-swatch-label]').forEach(function (label) {
      var input = label.querySelector('[data-qv-option]');
      var group = label.closest('.m-qv__opt');
      var picked = group ? group.querySelector('[data-qv-picked]') : null;
      if (!input || !picked) return;
      label.addEventListener('mouseenter', function () {
        picked.textContent = input.value;
      });
      label.addEventListener('mouseleave', syncPicked);
    });
  }

  function initQvContent(scope) {
    var root = scope.querySelector('[data-qv-root]');
    if (!root) return;

    var showMedia = initQvGallery(root);
    initQvVariants(root, showMedia);
    initQvWish(root);
    initQvDrag(root);

    // Keyboard opens only: a click or a tap leaves focus on the dialog, so no
    // button opens ringed. preventScroll: the button is pinned in view
    // already, and letting focus scroll to it opened the phone sheet 277px
    // down with the photo cut off.
    if (qvByPointer) return;
    var focusTarget = root.querySelector('[data-qv-atc]') || root.querySelector('a, button');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  // One request per product per page view. Hovering or focusing a trigger
  // starts it, so by the click the answer is usually already here.
  function fetchQuickView(url) {
    if (!qvCache[url]) {
      var joiner = url.indexOf('?') > -1 ? '&' : '?';
      qvCache[url] = fetch(url + joiner + 'section_id=minery-quickview')
        .then(function (response) {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .catch(function (err) {
          delete qvCache[url];
          throw err;
        });
    }
    return qvCache[url];
  }

  // Placeholder at the finished size — the dialog opens at the size it will
  // stay, instead of a 348px "Loading…" box that jumped to 603px.
  function skeletonMarkup() {
    return (
      '<div class="m-qv__grid is-skeleton" aria-busy="true">' +
        '<div class="m-qv__main">' +
          '<div class="m-qvg"><div class="m-qvg__track"><div class="m-qvg__slide m-qv__bone"></div></div></div>' +
          '<div class="m-qv__info">' +
            '<span class="m-qv__bone m-qv__bone--eyebrow"></span>' +
            '<span class="m-qv__bone m-qv__bone--title"></span>' +
            '<span class="m-qv__bone m-qv__bone--price"></span>' +
            '<span class="m-qv__bone m-qv__bone--line"></span>' +
            '<span class="m-qv__bone m-qv__bone--line"></span>' +
            '<span class="m-qv__bone m-qv__bone--line m-qv__bone--short"></span>' +
            '<span class="m-qv__bone m-qv__bone--dots"></span>' +
          '</div>' +
        '</div>' +
        '<div class="m-qv__foot"><span class="m-qv__bone m-qv__bone--button"></span></div>' +
        '<span class="visually-hidden" role="status" data-qv-status></span>' +
      '</div>'
    );
  }

  function loadQuickView(url, loadingText, errorText) {
    var body = dialog.querySelector('[data-m-qv-body]');
    if (!body) return;

    // A slow answer for a product the shopper has already left must not
    // replace the one they opened since.
    var token = {};
    qvToken = token;

    body.innerHTML = skeletonMarkup();
    var status = body.querySelector('[data-qv-status]');
    if (status) status.textContent = loadingText;

    // The card photo in place of the grey placeholder square.
    var standin = body.querySelector('.m-qvg__slide.m-qv__bone');
    if (standin && qvPhoto) {
      var img = document.createElement('img');
      img.src = qvPhoto;
      img.alt = '';
      standin.classList.remove('m-qv__bone');
      standin.appendChild(img);
    }

    openDialog();

    fetchQuickView(url)
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector('[data-m-qv-inner]');
        if (!inner) throw new Error('missing');
        return inner.innerHTML;
      })
      // Two-argument then: the error message below is for a product that
      // could not be fetched, not for a script fault after it arrived — that
      // should surface in the console, not replace a product that loaded.
      .then(function (markup) {
        if (qvToken !== token || !dialog.open) return;
        body.innerHTML = markup;
        initQvContent(body);
      }, function () {
        if (qvToken !== token) return;
        body.innerHTML = '<div class="m-qv__loading"><p></p></div>';
        var message = body.querySelector('p');
        message.textContent = errorText + ' ';
        var link = document.createElement('a');
        link.href = url;
        link.textContent = 'View the product page';
        message.appendChild(link);
        message.appendChild(document.createTextNode('.'));
      });
  }

  function initQuickView(el) {
    if (el.dataset.mReady === '1') return;
    el.dataset.mReady = '1';
    dialog = el;

    var close = dialog.querySelector('[data-m-qv-close]');
    if (close) close.addEventListener('click', closeDialog);

    // Clicking the backdrop reports the dialog itself as the target.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });

    // Escape leaves through the same animation as every other way out.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('close', function () {
      // The event is queued, not immediate. If a quick view was opened again
      // before it ran, emptying the body now would wipe the new product.
      if (dialog.open) return;

      // Chrome refuses a second cancelled Escape and closes outright, so the
      // closing state is cleared here too, not only after the animation.
      dialog.classList.remove('is-closing', 'is-dragging');
      dialog.style.transform = '';
      qvToken = null;
      var body = dialog.querySelector('[data-m-qv-body]');
      if (body) body.innerHTML = '';
      if (lastFocus && document.contains(lastFocus)) {
        if (qvByPointer) {
          lastFocus.classList.add('m-nofocusring');
          lastFocus.addEventListener('blur', function drop() {
            lastFocus.classList.remove('m-nofocusring');
            lastFocus.removeEventListener('blur', drop);
          });
        }
        lastFocus.focus({ preventScroll: true });
      }
      dialog.removeAttribute('tabindex');
    });

    // Adding to the cart from the dialog hands off to the cart drawer or
    // notification, which would otherwise open behind the backdrop.
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        closeDialog();
      });
    }
  }

  function prefetchQuickView(event) {
    var trigger = event.target.closest ? event.target.closest('[data-m-quickview]') : null;
    if (!trigger || !dialog) return;
    fetchQuickView(trigger.dataset.mQuickview).catch(function () {
      /* the click will try again and show the error */
    });
  }

  document.addEventListener('pointerover', prefetchQuickView, { passive: true });
  document.addEventListener('focusin', prefetchQuickView);

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-m-quickview]') : null;
    if (!trigger || !dialog) return;

    event.preventDefault();
    lastFocus = trigger;

    // detail is 0 for a keyboard activation, 1 or more for a tap or click.
    qvByPointer = event.detail > 0;

    var card = trigger.closest('.m-scard, .m-card, .m-relcard') || trigger;
    var cardImg = card.querySelector('img');
    qvPhoto = cardImg && cardImg.complete && cardImg.naturalWidth ? cardImg.currentSrc || cardImg.src : '';

    loadQuickView(
      trigger.dataset.mQuickview,
      trigger.dataset.mQuickviewLoading || 'Loading…',
      trigger.dataset.mQuickviewError || "This product couldn't be loaded."
    );
  });

  function boot(scope) {
    var ctx = scope || document;
    ctx.querySelectorAll('[data-m-circles]').forEach(initCircles);
    ctx.querySelectorAll('[data-m-filtertoggle]').forEach(initFilterToggle);
    ctx.querySelectorAll('[data-m-view]').forEach(initViewToggle);
    ctx.querySelectorAll('[data-m-sortform]').forEach(initSort);
    ctx.querySelectorAll('[data-m-qv]').forEach(initQuickView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
